import React, { useEffect, useMemo, useState } from 'react';

import { Button, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { Carousel } from 'react-responsive-carousel';

import { WhatsNewItem } from '@/components/WhatsNew/WhatsNewItemType';
import { ModalSheet } from '@/components/ui';
import { LS } from '@/constants/local-storage.constant';
import { SS } from '@/constants/session-storage.constant';
import { env } from '@/env';
import { QK } from '@/factories/query-key.factory';
import { createCMSClient } from '@/factories/url.factory';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

const cmsAPIClient = createCMSClient();

const WhatsNew = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const isDismissed = useMemo(() => {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    return sessionStorage.getItem(SS.IS_WHATS_NEW_DISMISSED);
  }, []);

  const { data, isPending } = useQuery({
    queryKey: QK.cms.whatsNew(),
    queryFn: async () => {
      const { data } = await cmsAPIClient.getAllWhatsNew();

      return data.data.attributes;
    },
    enabled: isDismissed !== 'true',
  });

  const items = useMemo(() => {
    if (data == null) {
      return [];
    }

    return data.items.filter(
      (item) => item[env.NEXT_PUBLIC_TOKEN] && !item.hidden,
    );
  }, [data]);

  const handleClose = () => {
    setShowModal(false);

    // ? storing in session storage to prevent api call on every page load
    sessionStorage?.setItem(SS.IS_WHATS_NEW_DISMISSED, 'true');

    if (data != null) {
      localStorage?.setItem(LS.WHATS_NEW_DISMISSED_VERSION, data.version);
    }
  };

  const handleNextClick = () => {
    if (data == null) {
      return void 0;
    }

    if (currentIndex === items.length - 1) {
      return handleClose();
    }

    setCurrentIndex((prev) => prev + 1);
  };

  useEffect(() => {
    if (data != null) {
      if (typeof localStorage !== 'undefined') {
        const dismissedVersion = localStorage.getItem(
          LS.WHATS_NEW_DISMISSED_VERSION,
        );

        if (dismissedVersion === data.version) {
          return () => void 0;
        }
      }
      setShowModal(true);
    }
  }, [data]);

  if (isPending || data == null) {
    return null;
  }

  return (
    <ModalSheet
      isOpen={showModal}
      onClose={handleClose}
      bodyProps={{ p: 0 }}
      footerProps={{
        px: {
          base: 4,
          md: 6,
        },
        pt: {
          base: 8,
          md: 9,
        },
        pb: {
          base: 5,
          md: 8,
        },
      }}
      footer={
        items[currentIndex] && (
          <Flex justifyContent="end" gap={4} p={0}>
            <Button
              as={Link}
              target={/^\//.test(items[currentIndex].link) ? '_self' : '_blank'}
              href={items[currentIndex].link}
              px={45}
              variant="outline"
              sx={{
                _hover: {
                  textDecoration: 'none',
                },
              }}
              onClick={handleClose}
            >
              Learn more
            </Button>
            <Button px={45} onClick={handleNextClick}>
              {currentIndex === items.length - 1 ? 'Got it' : 'Next'}
            </Button>
          </Flex>
        )
      }
    >
      <Carousel
        showArrows={false}
        showStatus={false}
        showThumbs={false}
        showIndicators={false}
        selectedItem={currentIndex}
        onChange={setCurrentIndex}
      >
        {items.map((item) => (
          <WhatsNewItem key={item.id} item={item} />
        ))}
      </Carousel>
    </ModalSheet>
  );
};

export { WhatsNew };
